/* eslint-disable react/prop-types */
import { useState } from 'react';
// @mui
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { recalculateAutoLeave } from '../../../services/leaveService';

const RESULT_MESSAGES = {
  removed: 'Auto leave removed',
  downgraded: 'Auto leave changed to a half day',
  upgraded: 'Auto leave changed to a full day',
  unchanged: 'No change to the auto leave',
};

/**
 * Shown on a day that has an auto leave. Admin/HR edit all of that day's logs first,
 * then click once to recalculate the leave from the final logged hours.
 */
export default function RecalculateLeaveButton({ userId, date, leave, totalTime, onRecalculated }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const dateStr = dayjs(date).format('YYYY-MM-DD');
  const loggedHours = (totalTime / (1000 * 60 * 60)).toFixed(2);
  const leaveLabel = leave?.isHalfDay ? 'half-day' : 'full-day';

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const response = await recalculateAutoLeave({ userId: Number(userId), date: dateStr });
      const result = response?.data?.data;
      toast.success(`${RESULT_MESSAGES[result?.action] || 'Leave recalculated'} (${result?.workedHours ?? 0}h logged)`);
      setOpen(false);
      onRecalculated?.();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to recalculate leave');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Alert
        severity="warning"
        sx={{ mt: 2, alignItems: 'center' }}
        action={
          <Button color="inherit" size="small" variant="outlined" onClick={() => setOpen(true)}>
            Recalculate Leave
          </Button>
        }
      >
        This day has a {leaveLabel} auto leave. If it was applied by mistake, edit the logs for this day and then
        click Recalculate Leave.
      </Alert>

      <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Recalculate auto leave</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Date: <strong>{dayjs(date).format('DD MMM YYYY')}</strong>
          </Typography>
          <Typography variant="body2" gutterBottom>
            Logged hours shown here: <strong>{loggedHours}h</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            6h or more removes the auto leave, 4–6h makes it a half day, under 4h makes it a full day. The leave
            balance is updated and the user gets an email.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirm} disabled={loading}>
            {loading ? 'Recalculating...' : 'Recalculate'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
